"use client";

import { createContext, Dispatch, JSX, ReactNode, RefObject, SetStateAction, use, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    BlockTypeSelect,
    BoldItalicUnderlineToggles,
    diffSourcePlugin,
    filter,
    InsertTable,
    linkDialogPlugin,
    linkPlugin,
    ListsToggle,
    markdownShortcutPlugin,
    MDXEditorMethods,
    realmPlugin,
    setMarkdown$,
    toolbarPlugin,
    UndoRedo,
} from "@mdxeditor/editor";
import { ForwardRefEditor } from "../richtext/ForwardRefEditor";
import { artifactsPlugin, caretScrollPlugin } from "../richtext/Artifact";
import { JsxElement } from "typescript";
import { appState, AppState } from "@/app/appState";
import { observer } from "mobx-react-lite";
import { Add_new, Add_new_neg, Back, Close, Edit, Publish, Remove, Save, Select_all, Update } from "./svgs";
import { shortenLabel } from "@/app/workspace/chat/chatObjectEditorWindow/chatObjectEditorWindow";
import { chatState } from "@/app/chatState";
import { runInAction } from "mobx";
import { editRequestWebSocket } from "@/aawebsocket/websocket.gen";
import { richTextContentPlugins } from "../richtext/richTextPlugins";
import { mergeEditorChange, prepareForEditor } from "../richtext/preserveSource";


export function useRefState<T>(initialValue: T): [RefObject<T>, (value: T) => void] {
    const ref = useRef<T>(initialValue);

    const setRefState = (value: T) => ref.current = value;

    return [ref, setRefState];
}

interface ModalContextValue {
    // Force-close this modal instance (bypasses any registered close handler). Use after a
    // confirmation step, or whenever the body decides the modal should actually close.
    dismiss: () => void;
    // Register a custom close handler invoked by the Modal's × / Escape / backdrop. Pass null to
    // clear it (e.g. on unmount).
    registerCloseHandler: (fn: (() => void) | null) => void;
}

export const ModalContext = createContext<ModalContextValue | null>(null);

// Lets a modal body component supply its own close behaviour to the enclosing Modal, so the
// Modal's single close affordances (× button, Escape, backdrop) drive it instead of the body
// drawing its own close buttons. The handler receives a `dismiss` to force-close the modal (e.g.
// after a confirmation). The handler is kept fresh via a ref so it always sees the latest
// component state without re-registering. Returns `dismiss` for convenience.
export function useModalClose(handler: (dismiss: () => void) => void): () => void {
    const ctx = useContext(ModalContext);
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    useEffect(() => {
        if (!ctx) return;
        ctx.registerCloseHandler(() => handlerRef.current(ctx.dismiss));
        return () => ctx.registerCloseHandler(null);
    }, [ctx]);

    return ctx?.dismiss ?? (() => { });
}

function parseKeyCombo(keyString: string) {
    const parts = keyString.split("+").map((part) => part.trim());

    return {
        shiftKey: parts.includes("shiftKey"),
        ctrlKey: parts.includes("ctrlKey"),
        altKey: parts.includes("altKey"),
        metaKey: parts.includes("metaKey"),
        key: parts.filter((part) => !["shiftKey", "ctrlKey", "altKey", "metaKey"].includes(part)),
    };
}


export function ChatListItem({
    children,
    onClick,
    isSelected,
    hasRevisions,
    hasChanges,
    isDisabled,
    isTransparent,
    leading,
    corner,
}: { children: ReactNode, onClick: (e: React.MouseEvent<HTMLDivElement>) => void, isSelected: boolean, hasRevisions?: boolean, hasChanges?: boolean, isDisabled: boolean, isTransparent?: boolean, leading?: ReactNode, corner?: ReactNode }): JSX.Element {
    const variant = isDisabled ? "nav-item-disabled" : isSelected ? "nav-item-selected" : isTransparent ? "nav-item-ghost" : "";
    return (
        <div className="flex flex-row w-full justify-start h-full">
            <div
                onClick={isDisabled ? undefined : onClick}
                title={children as string}
                className={`nav-item ${variant} ${leading ? "nav-item-leading" : ""}`}>
                {leading}
                <p className="l2 scrollbar-left-item overflow-x-clip">{shortenLabel(children as string, 20)}</p>
                {corner}
            </div>
            {hasRevisions && <div className="nav-item-flag-revisions" />}
            {hasChanges && <div className="nav-item-flag-changes" />}
        </div>
    );
}

export const ToolBox = (p: { is_small: boolean, children: any, intent?: string, lift?: boolean, user?: boolean, draft?: boolean, onClick?: () => void }) => {
    const intentEl = p.intent ? <small className="toolbox-intent">{p.intent}</small> : null;

    const body = p.is_small
        ? (
            <div className="toolbox-compact">
                {intentEl}
                <div className="toolbox-compact-content">{p.children}</div>
            </div>
        )
        : (
            <div
                onClick={p.onClick}
                className={[
                    "toolbox-body",
                    p.onClick && "toolbox-body-interactive",
                    p.user && "toolbox-body-user",
                    p.lift && p.user && "toolbox-body-lift",
                    p.draft && "toolbox-body-draft",
                ].filter(Boolean).join(" ")}
            >
                {intentEl}
                <div className="flex flex-row justify-start w-full">{p.children}</div>
            </div>
        );

    return <div className="toolbox">{body}</div>;
};

export function ActionHandler({
    selected,
    keysTrigger = "",
    title,
    children,
    funq,
    disabled,
    type,
    hidden,
    isButton = true,
    glow,
    className,
    capture,
}: {
    selected?: boolean;
    isButton?: boolean;
    title?: string;
    keysTrigger?: string;
    children?: React.ReactNode;
    funq: (clickEvent?: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void;
    disabled?: boolean;
    type?: "button" | "reset" | "submit" | undefined;
    hidden?: boolean | undefined;
    glow?: boolean;
    className?: string;
    // When true the key listener is registered in the capture phase, so it runs before
    // bubble-phase handlers elsewhere (e.g. a Modal claims Escape before background OEW handlers).
    capture?: boolean;
}) {
    useEffect(() => {
        const handleEvent = (e: KeyboardEvent) => {
            if (e.defaultPrevented) return;

            const keyCombo = parseKeyCombo(keysTrigger);

            // Check if the key combination matches
            const keyMatches =
                keyCombo.key.includes(e.key) ||
                (keyCombo.key.includes("Enter") && e.key === "Enter") ||
                (keyCombo.key.includes("Escape") && e.key === "Escape") ||
                (keyCombo.key.includes("Tab") && e.key === "Tab");

            const modifiersMatch =
                keyCombo.shiftKey === e.shiftKey &&
                keyCombo.ctrlKey === e.ctrlKey &&
                keyCombo.altKey === e.altKey &&
                keyCombo.metaKey === e.metaKey;

            if (keyMatches && modifiersMatch && !disabled) {
                const target = e.target as HTMLElement;
                const isInput = target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
                if (isInput && (e.key === "Delete" || e.key === "Backspace")) return;

                e.preventDefault();
                funq();
            }
        };

        document.addEventListener("keydown", handleEvent, capture);

        return () => {
            document.removeEventListener("keydown", handleEvent, capture);
        };
    }, [funq, keysTrigger, capture]);

    if (isButton) {
        return (
            <button
                title={title}
                id="Action handler button"
                type={type}
                onClick={(e) => funq(e)}
                disabled={disabled}
                hidden={hidden}
                className={["btn", glow && "btn-glow", selected && "btn-selected", className].filter(Boolean).join(" ")}
            >
                {children}
            </button>
        );
    } else {
        return <div id="Action handler"></div>;
    }
}

type StateType<Type> = {
    value: Type;
    label: string;
    readonly?: boolean;
    // onChange: Dispatch<SetStateAction<Type>>;
    onChange: (val: Type) => void;
    validator: (value: Type) => boolean;
};

export type TextInputType = StateType<string> & {
    type: "text" | "password";
};

export type TextAreaType = StateType<string | undefined> & {
    type: "textarea";
};

export type RichTextType = StateType<string | undefined> & {
    type: "richtext";
    artifactsComplete?: boolean;
    diffRootText?: string | null
};

type StateColumnType<Type> = {
    readonly?: boolean;
    hidden?: boolean;
    items: { id?: number; name: string, children?: ReactNode, className?: string }[] | undefined;
    search?: { get: string, set: (val: string) => void }
    pageNumber?: { get: number, set: (val: number) => void }
    type: "single" | "multiple" | "switch" | "datetimerange";
    foldoutLeft?: boolean
} & StateType<Type>;

export type entryProps = {
    onValidChange: (key: string, value: boolean) => void;
    edit: boolean | undefined;
};


export interface StateColumnSingleType extends StateColumnType<number | undefined> {
    items: { id: number; name: string, children?: ReactNode, className?: string }[] | undefined;
    type: "single";
}

export interface StateColumnMultipleType extends StateColumnType<number[]> {
    items: { id: number; name: string, children?: ReactNode, className?: string }[] | undefined;
    type: "multiple";
}

export interface StateColumnSwitchType extends StateColumnType<string | undefined> {
    items: { name: string }[] | undefined;
    type: "switch";
}

export type DateTimeRangeValue = { startTime: string; endTime: string };

export interface StateColumnDateTimeRangeType extends Omit<StateColumnType<DateTimeRangeValue>, 'items' | 'type'> {
    items?: undefined;
    type: "datetimerange";
    allDay?: boolean;
    onAllDayChange?: (allDay: boolean) => void;
}

function useValidator<Type>(x: {
    defaultValue: Type;
    label: string;
    onChange: (val: Type) => void;
    validator: (value: Type) => boolean;
    onValidChange: (key: string, value: boolean) => void;
}): [boolean | undefined, (val: Type, emitChange?: boolean) => void] {
    const onChangeRef = useRef(x.onChange);
    onChangeRef.current = x.onChange;
    const validatorRef = useRef(x.validator);
    validatorRef.current = x.validator;
    const onValidChangeRef = useRef(x.onValidChange);
    onValidChangeRef.current = x.onValidChange;

    const _valid = validatorRef.current(x.defaultValue);
    const [state, setState] = useState<boolean>(_valid);

    const setValidator = useCallback((val: Type, emitChange: boolean = true) => {
        const _valid = validatorRef.current(val);
        if (emitChange) {
            onChangeRef.current(val);
        }
        if (_valid !== state) {
            setState(_valid);
            onValidChangeRef.current(x.label, _valid);
        }
    }, [state, x.label]);

    useEffect(() => {
        onValidChangeRef.current(x.label, _valid);
    }, []);

    return [state, setValidator];
}

function StateColumnBox({
    label,
    valid,
    popupVisible,
    children,
    setPopupVisible,
    containerRef,
    popupRef
}: {
    label: string;
    valid: boolean | undefined;
    popupVisible?: boolean;
    children?: ReactNode;
    setPopupVisible?: Dispatch<SetStateAction<boolean>>;
    containerRef: RefObject<HTMLDivElement>;
    popupRef?: RefObject<HTMLDivElement>;
}) {
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node) &&
                (!popupRef?.current || !popupRef.current.contains(event.target as Node)) &&
                popupVisible &&
                setPopupVisible
            ) {
                setPopupVisible(false);
            }
        };

        if (popupVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [popupVisible, setPopupVisible, containerRef, popupRef]);

    return (
        <div
            ref={containerRef}
            className={["value-box", popupVisible && "value-box-open", !valid && "value-box-invalid"].filter(Boolean).join(" ")}
        >
            {children}
        </div>
    );
}


type ColumnViewProps<T> = {
    setPopupVisible: Dispatch<SetStateAction<boolean>>,
    setSearch: Dispatch<SetStateAction<string>>,
    value: any,
    valid: boolean | undefined,
    popupVisible: boolean,
    x: StateColumnType<T> & entryProps,
    callback: (value: any, y: { id?: number; name: string }) => boolean;

}

function ColumnView<T>(props: ColumnViewProps<T>) {
    const items = useMemo(() => props.x.items, [props.value, props.x.items]);

    return (
        <div className="flex flex-row items-center h-full">
            <label htmlFor={props.x.label} className="value-box-label">
                {props.x.label}
            </label>
            <div className="flex flex-row justify-between items-center w-full gap-[6px] h-full">
                <div className="value-box-values">
                    {items
                        ?.filter((y) => props.callback(props.value, y))
                        .map((i) => (
                            <span key={i.name} title={i.name} className={`chip ${i.className ?? ""}`}>
                                {i.children || shortenLabel(i.name)}
                            </span>
                        ))}
                    {!props.x.edit && !items?.some((y) => props.callback(props.value, y)) && (
                        <span className="value-box-empty">—</span>
                    )}
                </div>
                {props.x.edit && !props.x.readonly && (
                    <span className="icon-btn" onClick={() => props.setPopupVisible((v) => !v)}>
                        {props.popupVisible ? <Add_new_neg size={16} /> : <Add_new size={16} />}
                    </span>
                )}
            </div>
        </div>
    )
}

export const OptionIndicator = (props: { selected: boolean }) => (
    <span className={`option-indicator ${props.selected ? "option-indicator-selected" : ""}`} />
);

interface UseColumnStateResult<T> {
    value: T;
    setValue: (val: T) => void;
    valid: boolean | undefined;
    setValidator: (val: T, emitChange?: boolean) => void;
    popupVisible: boolean;
    setPopupVisible: Dispatch<SetStateAction<boolean>>;
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
}

// Reusable hook with proper typing
function useColumnState<T>(props: {
    value: T;
    onChange: (val: T) => void;
    validator: (val: T) => boolean;
    label: string;
    edit?: boolean;
    search?: { set: (val: string) => void };
    onValidChange: (key: string, value: boolean) => void;
}): UseColumnStateResult<T> {

    const [popupVisible, setPopupVisible] = useState(false);
    const [search, setSearch] = useState<string>("");
    const [value, setValue] = useState(props.value);

    const [valid, setValidator] = useValidator({
        defaultValue: props.value,
        label: props.label,
        onChange: (val) => {
            setValue(val);
            props.onChange(val);
        },
        onValidChange: props.onValidChange,
        validator: props.validator,
    });

    useEffect(() => {
        setValue(props.value);
        setValidator(props.value, false);
    }, [props.value, props.validator]);


    useEffect(() => {
        if (!props.edit) {
            setPopupVisible(false);
        }
    }, [props.edit]);

    useEffect(() => {
        props.search?.set(search);
    }, [search]);

    return {
        value,
        setValue,
        valid,
        setValidator,
        popupVisible,
        setPopupVisible,
        search,
        setSearch
    };
}

function StateColumnPopup<T>({ state, items, onClick, selected, triggerRef, popupRef }: {
    state: UseColumnStateResult<T>,
    items: { id?: number; name: string }[] | undefined,
    onClick: (id?: number, name?: string) => void,
    selected: (id?: number, name?: string) => boolean,
    triggerRef: RefObject<HTMLDivElement>,
    popupRef: RefObject<HTMLDivElement>
}) {
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

    useEffect(() => {
        if (state.popupVisible && triggerRef.current) {
            const updatePosition = () => {
                if (triggerRef.current) {
                    const rect = triggerRef.current.getBoundingClientRect();
                    setCoords({
                        top: rect.bottom + 4,
                        left: rect.left,
                        width: rect.width
                    });
                }
            };
            updatePosition();
            const handleScroll = (e: Event) => {
                if (popupRef.current && e.target instanceof Node && popupRef.current.contains(e.target)) {
                    return;
                }
                state.setPopupVisible(false);
            };
            window.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', handleScroll);
            return () => {
                window.removeEventListener('scroll', handleScroll, true);
                window.removeEventListener('resize', handleScroll);
            };
        }
    }, [state.popupVisible, triggerRef]);

    if (!state.popupVisible) return null;

    // Use portal to escape any transformed parents (like modals) that break fixed positioning
    if (typeof document === 'undefined') return null;

    return createPortal(
        <div
            ref={popupRef}
            className="popover"
            style={{
                top: coords.top,
                left: coords.left,
                minWidth: coords.width,
            }}
        >
            <input
                className="popover-filter"
                type="text"
                value={state.search}
                onChange={(e) => state.setSearch(e.target.value)}
                placeholder={appState.t("common.filterPlaceholder")}
                autoFocus
                onClick={(e) => e.stopPropagation()}
            />
            {items?.map((item) => {
                const isSelected = selected(item.id, item.name);
                return (
                    <button
                        key={item.id ?? item.name}
                        onClick={() => onClick(item.id, item.name)}
                        className={`option ${isSelected ? "option-selected" : ""}`}
                    >
                        <OptionIndicator selected={isSelected} />
                        <span className="option-label">{item.name}</span>
                    </button>
                );
            })}
        </div>,
        document.body
    );
}

export const StateColumnSingle = observer(function StateColumnSingle(x: StateColumnSingleType & entryProps) {
    const state = useColumnState(x)
    const containerRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    return (
        <StateColumnBox label={x.label} valid={state.valid} popupVisible={state.popupVisible} setPopupVisible={state.setPopupVisible} containerRef={containerRef} popupRef={popupRef}>
            <ColumnView x={x} callback={(value, y) => { return value == y.id }} setPopupVisible={state.setPopupVisible} setSearch={state.setSearch} valid={state.valid} value={state.value} popupVisible={state.popupVisible} />
            <StateColumnPopup
                state={state}
                items={x.items}
                onClick={(id) => { state.setValidator(id === state.value ? undefined : id) }}
                selected={(id) => { return id === state.value }}
                triggerRef={containerRef}
                popupRef={popupRef}
            />
        </StateColumnBox>
    );
});

export const StateColumnMultiple = observer(function StateColumnMultiple(x: StateColumnMultipleType & entryProps) {
    const state = useColumnState(x)
    const containerRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    return (
        <StateColumnBox label={x.label} valid={state.valid} popupVisible={state.popupVisible} setPopupVisible={state.setPopupVisible} containerRef={containerRef} popupRef={popupRef}>
            <ColumnView x={x} callback={(value, y) => { return value?.includes(y.id) }} setPopupVisible={state.setPopupVisible} setSearch={state.setSearch} valid={state.valid} value={state.value} popupVisible={state.popupVisible} />
            <StateColumnPopup
                state={state}
                items={x.items}
                onClick={(id) => {
                    if (id) {
                        state.value.includes(id)
                            ? state.setValidator(state.value.filter((y) => y != id))
                            : state.setValidator([...state.value, id]);
                    }
                }}
                selected={(id) => { return state.value?.includes(id ?? -1) }}
                triggerRef={containerRef}
                popupRef={popupRef}
            />
        </StateColumnBox>
    );
});

export const StateColumnSwitch = observer(function StateColumnSwitch(x: StateColumnSwitchType & entryProps) {
    const state = useColumnState(x)
    const containerRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    return (
        <StateColumnBox label={x.label} valid={state.valid} popupVisible={state.popupVisible} setPopupVisible={state.setPopupVisible} containerRef={containerRef} popupRef={popupRef}>
            <ColumnView x={x} callback={(value, y) => { return value == y.name }} setPopupVisible={state.setPopupVisible} setSearch={state.setSearch} valid={state.valid} value={state.value} popupVisible={state.popupVisible} />
            <StateColumnPopup
                state={state}
                items={x.items}
                onClick={(id, name) => { state.setValidator(name) }}
                selected={(id, name) => { return name === state.value }}
                triggerRef={containerRef}
                popupRef={popupRef}
            />
        </StateColumnBox>
    );
});

export const StateColumnDateTimeRange = observer(function StateColumnDateTimeRange(x: StateColumnDateTimeRangeType & entryProps) {
    const [valid, setValidator] = useValidator<DateTimeRangeValue>({
        defaultValue: x.value,
        label: x.label,
        onChange: x.onChange,
        onValidChange: x.onValidChange,
        validator: x.validator,
    });

    useEffect(() => {
        setValidator(x.value, false);
    }, [x.value.startTime, x.value.endTime]);

    // Convert ISO string to datetime-local or date input value (local time)
    const toLocalInput = (iso: string, dateOnly: boolean) => {
        if (!iso) return "";
        const d = new Date(iso);
        if (dateOnly) return d.toISOString().slice(0, 10);
        // yyyy-MM-ddTHH:mm format for datetime-local
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const startIso = val ? new Date(val).toISOString() : "";
        // Preserve duration when start changes
        if (startIso && x.value.endTime && x.value.startTime) {
            const prevDuration = new Date(x.value.endTime).getTime() - new Date(x.value.startTime).getTime();
            const newEnd = new Date(new Date(startIso).getTime() + prevDuration).toISOString();
            setValidator({ startTime: startIso, endTime: newEnd });
        } else {
            setValidator({ startTime: startIso, endTime: x.value.endTime });
        }
    };

    const durationHHMM = useMemo(() => {
        if (!x.value.startTime || !x.value.endTime) return "01:00";
        const mins = Math.round((new Date(x.value.endTime).getTime() - new Date(x.value.startTime).getTime()) / 60000);
        const h = Math.min(Math.max(Math.floor(mins / 60), 0), 23);
        const m = Math.max(mins % 60, 0);
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }, [x.value.startTime, x.value.endTime]);

    const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const [hStr, mStr] = e.target.value.split(":");
        const totalMins = (parseInt(hStr) || 0) * 60 + (parseInt(mStr) || 0);
        if (x.value.startTime && totalMins > 0) {
            const newEnd = new Date(new Date(x.value.startTime).getTime() + totalMins * 60000).toISOString();
            setValidator({ startTime: x.value.startTime, endTime: newEnd });
        }
    };

    const containerRef = useRef<HTMLDivElement>(null);

    const formatDisplay = (iso: string) => {
        if (!iso) return "";
        try {
            return new Date(iso).toLocaleString("sv-SE", { hour12: false, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
        } catch { return iso; }
    };

    const formatDuration = (mins: number) => {
        if (mins < 60) return `${mins} min`;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return m ? `${h} h ${m} min` : `${h} h`;
    };

    // For readonly display
    const currentDurationMins = useMemo(() => {
        if (!x.value.startTime || !x.value.endTime) return 60;
        return Math.round((new Date(x.value.endTime).getTime() - new Date(x.value.startTime).getTime()) / 60000);
    }, [x.value.startTime, x.value.endTime]);

    return (
        <div className={`value-box ${valid ? "" : "value-box-invalid"}`} ref={containerRef}>
            <div className="flex flex-row items-center h-full">
                <label className="value-box-label">{x.label}</label>
                {x.edit && !x.readonly ? (
                    <div className="value-box-fields">
                        <input
                            type="datetime-local"
                            value={toLocalInput(x.value.startTime, false)}
                            onChange={handleStartChange}
                            className="input-bare"
                        />
                        <span className="value-box-label px-0">for</span>
                        <input
                            type="time"
                            value={durationHHMM}
                            onChange={handleDurationChange}
                            className="input-bare"
                        />
                    </div>
                ) : (
                    <div className="value-box-values">
                        {x.value.startTime && x.value.endTime
                            ? <span className="chip">{`${formatDisplay(x.value.startTime)} (${formatDuration(currentDurationMins)})`}</span>
                            : <span className="value-box-empty">—</span>}
                    </div>
                )}
            </div>
        </div>
    );
});

export function NameInput(x: TextInputType & entryProps) {
    const [valid, setValidator] = useValidator<string>({
        defaultValue: x.value,
        label: x.label,
        onChange: x.onChange,
        onValidChange: x.onValidChange,
        validator: x.validator,
    });
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!inputRef.current) return;
        // Don't overwrite the DOM value while the user is typing — that steals focus / resets the caret.
        if (document.activeElement === inputRef.current) return;
        inputRef.current.value = x.value;
        setValidator(x.value, false);
    }, [x.value]);

    return (
        <input
            ref={inputRef}
            type={x.type}
            title={x.label}
            defaultValue={x.value}
            onChange={(p) => {
                setValidator(p.target.value);
            }}
            readOnly={!x.edit || x.readonly}
            required
            className={`input input-entry ${valid ? "" : "input-invalid"}`}
        />
    );
}

/** Plain multi-line entry field (e.g. event / client description). Grows with its content. */
export function EntryTextArea(x: TextAreaType & entryProps) {
    const [valid, setValidator] = useValidator<string | undefined>({
        defaultValue: x.value,
        label: x.label,
        onChange: x.onChange,
        onValidChange: x.onValidChange,
        validator: x.validator,
    });
    const ref = useRef<HTMLTextAreaElement>(null);

    const fit = () => {
        const el = ref.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight + 2}px`;
    };

    useEffect(() => {
        if (!ref.current) return;
        if (document.activeElement !== ref.current) {
            ref.current.value = x.value ?? "";
            setValidator(x.value, false);
        }
        fit();
    }, [x.value, x.edit]);

    return (
        <textarea
            ref={ref}
            title={x.label}
            defaultValue={x.value ?? ""}
            rows={2}
            onChange={(p) => {
                setValidator(p.target.value);
                fit();
            }}
            readOnly={!x.edit || x.readonly}
            className={`textarea textarea-entry ${valid ? "" : "input-invalid"}`}
        />
    );
}

// BEWARE - the MDX editor turns the last space of trailing white space into the HTML encoding &#x20;
const RichTextEditor = observer(function RichTextEditor(x: RichTextType & entryProps) {
    const defaultValue = x.value;


    const [valid, setValidator] = useValidator<string>({
        defaultValue: defaultValue ?? "",
        label: x.label,
        onChange: x.onChange,
        onValidChange: x.onValidChange,
        validator: x.validator,
    });
    const plugins = [
        ...richTextContentPlugins(),
        //toolbar plugin needs this css to avoid glitching...
        toolbarPlugin({
            toolbarClassName: `richtext-toolbar ${x.edit ? "" : "!hidden"}`,
            toolbarContents: () => (
                <>
                    <UndoRedo />
                    <BlockTypeSelect />
                    <BoldItalicUnderlineToggles />
                    <ListsToggle />
                    <InsertTable />
                </>
            ),
        }),
    ];

    if (x.diffRootText)
        plugins.push(
            diffSourcePlugin({
                diffMarkdown: x.diffRootText,
                viewMode: 'diff',
                readOnlyDiff: true
            })
        )

    plugins.push(caretScrollPlugin());

    if (x.artifactsComplete) {
        plugins.push(artifactsPlugin());
    }

    const editorRef = useRef<MDXEditorMethods>(null);
    const editorFocusedRef = useRef(false);
    // The markdown the editor was loaded with, and the editor's own serialization of it
    // before the user's first change. Edits are patched onto the source (see mergeEditorChange).
    const sourceRef = useRef(defaultValue ?? "");
    const baselineRef = useRef<string | null>(null);
    // Recent values this editor emitted. While focused, `value` coming back as one of these is
    // the echo of the user's own typing; any other value (an incoming revision, a save) replaces
    // the editor content. Several are kept because echoes can lag behind fast typing.
    const emittedRef = useRef<string[]>([]);

    useEffect(() => {
        const isEcho = emittedRef.current.includes(defaultValue ?? "");
        if (editorFocusedRef.current && isEcho) return;
        emittedRef.current = [];
        sourceRef.current = defaultValue ?? "";
        baselineRef.current = null;
        editorRef.current?.setMarkdown(prepareForEditor(defaultValue ?? ""));
        // setMarkdown commits in a Lexical microtask queued before this one, so the
        // baseline is read from the freshly loaded content.
        if (editorFocusedRef.current) queueMicrotask(() => { baselineRef.current = editorRef.current?.getMarkdown() ?? null; });
        setValidator(defaultValue ?? "", false);
    }, [x.value]);

    return (
        <div
            onFocusCapture={() => {
                editorFocusedRef.current = true;
                if (baselineRef.current === null) baselineRef.current = editorRef.current?.getMarkdown() ?? null;
            }}
            onBlurCapture={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                    editorFocusedRef.current = false;
                }
            }}
            className={["richtext", x.edit && "richtext-editing", x.edit && !valid && "richtext-invalid"].filter(Boolean).join(" ")}
        >
            <ForwardRefEditor
                ref={editorRef}

                plugins={plugins}
                markdown={prepareForEditor(defaultValue ?? "")}
                readOnly={!x.edit}
                onChange={(p, isInit) => {
                    // Changes the editor makes by itself (load normalisation, citation
                    // processing) are not user edits and must not be saved.
                    if (isInit || !editorFocusedRef.current) return;
                    const next = baselineRef.current === null ? p : mergeEditorChange(sourceRef.current, baselineRef.current, p);
                    emittedRef.current = [...emittedRef.current.slice(-9), next];
                    setValidator(next);
                }}
                contentEditableClassName="richtext-content prose serif"
                placeholder={x.edit ? "Enter some text here..." : ""}
                suppressHtmlProcessing={false}
            />
        </div>
    );
});

type Modal = {
    get: { title: string; body: string; comment?: { name: string; val: string }; action?: () => void } | undefined;
    set: (val: { title: string; body: string; comment?: { name: string; val: string }; action?: () => void } | undefined) => void;
};

type IdParams = {
    singular: { get?: number; set: (val: number) => void };
    plural: {
        get: number[];
        set: (valOrUpdater: number[] | ((prev: number[]) => number[])) => void;
    };
};

export type EntryListItemProps = {
    loading: boolean;
    item: { id: number; val: string; highlighted?: boolean; readonly?: boolean; showonly?: boolean; scope?: "tag" | "agent" | "artifact"; lock: boolean };
    idParams: IdParams;
    readonly?: boolean;
    deleteOnly?: boolean;
};

export const EntryListItemDefault = observer((props: EntryListItemProps) => {
    const { item } = props;
    const selectable = !item.showonly && !item.readonly && !item.lock;
    const selected = props.idParams.plural.get.includes(item.id) && !item.showonly && !item.readonly;
    const rowVariant = item.highlighted ? "entry-row-highlighted" : item.showonly ? "entry-row-static" : "";
    const markVariant = selected ? "entry-row-check-mark-selected" : selectable ? "entry-row-check-mark-selectable" : "";

    return (
        <div key={item.id} className={`entry-row ${rowVariant}`} data-item-id={item.id}>
            {!props.readonly && (
                <div
                    className="entry-row-check"
                    onClick={() => {
                        if (!selectable) return;
                        props.idParams.plural.set((p) => p.includes(item.id) ? p.filter((y) => y != item.id) : [...p, item.id]);
                    }}
                >
                    <div className={`entry-row-check-mark ${markVariant}`}>{item.showonly && "↓"}</div>
                </div>
            )}
            <div
                className={`entry-row-label ${item.showonly ? "cursor-default" : ""}`}
                onClick={() => {
                    if (!item.showonly) props.idParams.singular.set(item.id);
                }}
            >
                <p className="l2">{item.val}</p>
            </div>
        </div>
    );
});


export const EntryList = observer(function EntryList({
    name,
    subtitle,
    loading,
    deleting,
    deleteEntries,
    idParams,
    readonly = false,
    deleteonly = false,
    search,
    pageNumber,
    items,
    stateColumns,
    EntryListItem = EntryListItemDefault,
}: {
    name: string;
    subtitle?: string;
    loading: boolean;
    deleting: boolean;


    EntryListItem?: (props: EntryListItemProps) => JSX.Element;

    search?: { get: string, set: (val: string) => void };

    pageNumber?: { get: number, set: (val: number) => void }

    deleteonly?: boolean;

    readonly?: boolean;

    deleteEntries?: () => void;

    stateColumns?: (StateColumnMultipleType | StateColumnSingleType | StateColumnSwitchType | StateColumnDateTimeRangeType)[];



    idParams: IdParams;

    items: {
        id: number;
        name?: string;
        children?: ReactNode;
        highlighted?: boolean;
        readonly?: boolean;
        lock: boolean
    }[] | undefined;
}) {


    const [valid, setValid] = useState(false);



    const [_, setValids] = useState<{ [key: string]: boolean }>({});



    const updateValids = useCallback((key: string, value: boolean) => {
        setValids((prev) => {
            if (prev[key] === value) return prev;
            const newValids = { ...prev, [key]: value };
            const newValid = Object.values(newValids).every((v) => v);
            if (newValid !== valid)
                setValid(newValid);

            return newValids;
        });
    }, [valid]);

    useEffect(() => {
        if (stateColumns === undefined) {
            setValid(true);
        }
    }, []);
    return (
        <div className="z-10 p-1">
            <div className="h-fit gap-1 items-center w-full flex flex-col justify-between">
                <div className="list-header" title={subtitle ? `${name} - ${subtitle}` : name}>
                    <small className="meta">{name}</small>
                    {subtitle && <small className="meta">-</small>}
                    {subtitle && <small className="meta-strong">{subtitle}</small>}
                </div>

                <div className="flex flex-row h-full items-center gap-1 w-full">
                    {items && search && ((items.length > 10) || search.get) && (
                        <input
                            className="search"
                            type="text"
                            value={search.get}
                            onChange={(e) => search.set(e.target.value)}
                            placeholder={appState.t("common.filterPlaceholder")}
                        />
                    )}
                    {stateColumns && <div className="flex flex-row gap-1 w-full h-full">

                        {stateColumns
                            ?.filter((col): col is StateColumnSingleType => col.type === "single")
                            .map((x) => (
                                <StateColumnSingle key={x.label} {...x} edit={true} onValidChange={updateValids} />
                            ))}

                        {/* Multiple columns with proper type narrowing */}
                        {stateColumns
                            ?.filter((col): col is StateColumnSwitchType => col.type === "switch")
                            .map((x) => (
                                <StateColumnSwitch key={x.label} {...x} edit={true} onValidChange={updateValids} />
                            ))}
                        {stateColumns
                            ?.filter((col): col is StateColumnMultipleType => col.type === "multiple")
                            .map((x) => (
                                <StateColumnMultiple key={x.label} {...x} edit={true} onValidChange={updateValids} />
                            ))}
                        {stateColumns
                            ?.filter((col): col is StateColumnDateTimeRangeType => col.type === "datetimerange")
                            .map((x) => (
                                <StateColumnDateTimeRange key={x.label} {...x} edit={true} onValidChange={updateValids} />
                            ))}

                    </div>
                    }


                    <div className="w-auto flex flex-row flex-grow justify-end gap-1">
                        {
                            !readonly && !deleteonly ?

                                <ActionHandler
                                    keysTrigger="ctrlKey + w"
                                    funq={() => {
                                        idParams.singular.set(-1);
                                    }}
                                    disabled={loading || !valid}
                                    hidden={idParams.singular.get != null || loading}
                                >
                                    <Add_new />
                                </ActionHandler>
                                :
                                null
                        }
                        <ActionHandler
                            keysTrigger="Escape"
                            funq={async () => {
                                idParams.plural.set([]);
                            }}
                            hidden={!(idParams.plural.get.length > 0) || loading}
                            disabled={loading || !(idParams.plural.get.length > 0)}
                        >
                            {appState.t("common.clearSelection")}
                        </ActionHandler>

                        <ActionHandler
                            keysTrigger="ctrlKey + a"
                            funq={async () => {
                                idParams.plural.set(items?.filter(x => !x.readonly).map(x => x.id) ?? []);
                            }}
                            hidden={readonly || items?.filter(x => !x.readonly).map(x => x.id).every((x) => idParams.plural.get.includes(x)) || loading}
                            disabled={loading || items?.filter(x => !x.readonly).map(x => x.id).every((x) => idParams.plural.get.includes(x))}
                        >
                            <Select_all />
                        </ActionHandler>

                        {deleteEntries && !readonly ? (
                            <ActionHandler
                                keysTrigger="Delete"
                                funq={async () => {
                                    appState.setModal({
                                        title: appState.t("common.confirmTitle"),
                                        body: appState.t("common.confirmIrreversible"),
                                        action: deleteEntries,
                                    });
                                }}
                                hidden={!(idParams.plural.get.length > 0 || loading)}
                                disabled={deleting || !(idParams.plural.get.length > 0)}
                            >
                                <Remove />
                            </ActionHandler>
                        ) : (
                            ""
                        )}
                    </div>

                </div>
            </div>
            {items?.map((x, i) => {
                return (
                    <div key={i}>
                        <EntryListItem loading={loading} deleteOnly={deleteonly} readonly={readonly} idParams={idParams} item={x} />
                    </div>
                );
            })}
        </div>

    );
});

export const Modal = observer(function Modal({
    get,
    set,
    loading,
    isTop = true,
}: {
    get: { title: string; body?: React.ReactNode; comment?: { name: string; val: string }; action?: () => void, close?: (() => void) | null } | undefined;
    set: (modal: { title: string; body: React.ReactNode; comment?: { name: string; val: string }; action?: () => void } | undefined) => void;
    loading: boolean;
    // Whether this modal is the topmost one in the stack. Only the top modal reacts to Escape /
    // backdrop / action keys, so keyboard close always targets the modal the user actually sees
    // (a disabled ActionHandler doesn't preventDefault, letting the top one's handler win).
    isTop?: boolean;
}) {

    useEffect(() => {
        console.log("mount modal", get)
    }, [get]);

    // A modal body (via `useModalClose`) can register its own close behaviour here; the Modal's
    // single close affordances (× / Escape / backdrop) route through it. Falls back to the default
    // dismissal when nothing is registered.
    const closeHandlerRef = useRef<(() => void) | null>(null);
    const [hasCustomHandler, setHasCustomHandler] = useState(false);
    const registerCloseHandler = useCallback((fn: (() => void) | null) => {
        closeHandlerRef.current = fn;
        setHasCustomHandler(!!fn);
    }, []);
    const setRef = useRef(set);
    setRef.current = set;
    const dismiss = useCallback(() => setRef.current(undefined), []);
    const modalContextValue = useMemo(() => ({ dismiss, registerCloseHandler }), [dismiss, registerCloseHandler]);

    if (!get) {
        return
    }

    const requestClose = () => {
        if (closeHandlerRef.current) {
            closeHandlerRef.current();
            return;
        }
        if (get.close === null) return;
        get.close ? get.close() : set(undefined);
    };

    const dismissable = get.close !== null || hasCustomHandler;

    return (
        <div className="modal">
            <div
                onClick={() => { if (isTop) requestClose(); }}
                className={`modal-backdrop ${dismissable ? "modal-backdrop-dismissable" : ""}`}
            />
            <div className="modal-frame">
                <ModalPanel
                    title={get.title}
                    close={dismissable && (
                        <ActionHandler keysTrigger="Escape" funq={() => requestClose()} disabled={loading || !isTop} capture>
                            ×
                        </ActionHandler>
                    )}
                    footer={get.action && (
                        <>
                            <ActionHandler disabled={loading || !isTop} keysTrigger={"Escape"} funq={() => set(undefined)} capture>
                                {appState.t("common.cancel")}
                            </ActionHandler>
                            <ActionHandler
                                keysTrigger={"Enter"}
                                disabled={loading || !isTop}
                                funq={() => {
                                    set(undefined);
                                    get.action?.();
                                }}
                            >
                                {appState.t("common.proceed")}
                            </ActionHandler>
                        </>
                    )}
                >
                    <ModalContext.Provider value={modalContextValue}>
                        <div className="modal-content">{typeof get.body === 'string' ? <p className="p-2">{get.body}</p> : get.body}</div>
                    </ModalContext.Provider>
                    {get.comment && (
                        <div className="flex flex-row gap-3 px-1">
                            <p className="b select-none">{get.comment.name}</p>
                            <p className="l">{get.comment.val}</p>
                        </div>
                    )}
                </ModalPanel>
            </div>
        </div>
    );
});

/** Visual shell of a modal (title, close slot, body, footer). `Modal` adds the overlay and behaviour. */
export const ModalPanel = ({ title, close, footer, children }: { title: ReactNode; close?: ReactNode; footer?: ReactNode; children?: ReactNode }) => (
    <div onClick={(e) => e.stopPropagation()} className="modal-panel">
        <div className="modal-header">
            <p className="modal-title">{title}</p>
            {close}
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
    </div>
);

export const DefEdit = async (val: boolean, id: number | undefined, setEdit: (val: boolean) => void, object_type: "artifact" | "tag" | "agent" | "organisation" | "client", organisationId?: number) => {
    if (val)
        if (id && id > 0)
            await editRequestWebSocket(id, (res) => {
                // WS sends the object id on a successful lock, or nothing on failure.
                // Do not compare that id to the current user — root org (id 1) often
                // matches admin user id 1 and would never enter edit mode.
                setEdit(res != null);
            }, object_type, undefined, organisationId)
        else
            setEdit(true)
    else
        setEdit(false)

}
export const EntryItem = observer(({
    lock,
    setEdit,
    name,
    forcedEdit = false,
    unsavedChanges = true,
    created_at,
    loading,
    deleting,
    deleteEntry,
    updateEntry,
    createEntry,
    idParams,
    refColumns,
    nameColumn,
    stateColumns,
    chatEditorWindowClose,
    refreshCount: refreshCount,
    chatForm,
    readonly = false,
    publishDraft = false,
    edit = false,
    isModal = false,
    isRevisionRoot = false,
    headerClickNavigateFunction
}: {
    name: string;
    created_at?: string;
    lock: boolean;
    forcedEdit?: boolean;
    unsavedChanges?: boolean;
    setEdit: (val: boolean) => void
    loading: boolean;
    edit?: boolean
    isModal?: boolean
    isRevisionRoot?: boolean
    headerClickNavigateFunction?: () => void
    chatEditorWindowClose?: () => void
    deleting: boolean;
    deleteEntry?: () => void;
    updateEntry?: () => Promise<boolean | undefined>;
    createEntry?: () => Promise<boolean | undefined>;

    readonly?: boolean

    publishDraft?: (() => Promise<boolean | undefined>) | false;

    chatForm?: boolean

    idParams: {
        singular: { get: number | undefined; set: (val: number | undefined) => void };
        plural: {
            get: number[];
            set: (valOrUpdater: number[] | ((prev: number[]) => number[])) => void;
        };
    };



    refColumns?: (TextInputType | TextAreaType | RichTextType)[] | undefined;

    nameColumn: TextInputType | undefined;

    stateColumns?: (StateColumnMultipleType | StateColumnSingleType | StateColumnSwitchType | StateColumnDateTimeRangeType | undefined)[];

    refreshCount: number;
}) => {



    const [valid, setValid] = useState(false);
    const [localRefreshCount, setLocalRefreshCount] = useState(0);

    const [_, setValids] = useState<{ [key: string]: boolean }>({});

    const updateValids = useCallback((key: string, value: boolean) => {
        setValids((prev) => {
            if (prev[key] === value) return prev;
            const newValids = { ...prev, [key]: value };
            const newValid = Object.values(newValids).every((v) => v);
            if (newValid !== valid)
                setValid(newValid);

            return newValids;
        });
    }, [valid]);



    const hasBody = refColumns?.some((x) => (x.value && x.value.length > 0) || edit) ?? false;

    return (
        <div key={localRefreshCount} onClick={() => {
            headerClickNavigateFunction && headerClickNavigateFunction()
        }} className={`entry ${headerClickNavigateFunction ? "entry-link" : ""}`}>
            {/* Direct child of .entry so it can stick for the whole entry, not just the header. */}
            <div className={`entry-meta ${chatEditorWindowClose ? "entry-meta-sticky" : ""}`}>
                    <div className={`edit-stripe ${(chatForm || chatEditorWindowClose) && (forcedEdit || edit) ? (unsavedChanges ? "edit-stripe-dirty" : "edit-stripe-clean") : ""}`}></div>

                    <div className="entry-inset-x flex flex-row items-center justify-between gap-1 pt-[0.375rem] pb-1">
                        <small className="entry-kind" title={name}>{name}</small>
                        <small className="meta shrink-0">{created_at}</small>
                        {chatEditorWindowClose && (
                            <ActionHandler
                                className="btn-ghost btn-meta"
                                title={appState.t("common.close")}
                                keysTrigger="Escape"
                                funq={async () => {
                                    runInAction(async () => {
                                        await setEdit(false);
                                        chatEditorWindowClose();
                                        idParams.singular.set(undefined);
                                    });
                                }}
                                disabled={loading}
                            >
                                <Close />
                            </ActionHandler>
                        )}
                    </div>
            </div>
            <div className="entry-header">
                <div className="entry-inset-x gap-1 items-left w-full flex flex-col justify-between"
                >
                    {/*NAme */}
                    <div className="entry-toolbar">
                        {nameColumn &&
                            <div

                                className="flex flex-row items-center gap-1 grow">
                                <NameInput {...nameColumn} onValidChange={updateValids} edit={edit ?? false} />

                            </div>}
                        {(!chatForm || (publishDraft && !readonly && !(idParams.singular.get == -1 || loading))) && ( // buttons container
                            <div className=" flex flex-row justify-end">

                                {!(chatForm && !edit) && (
                                    <div className="entry-actions">
                                        {
                                            deleteEntry && !edit && !readonly ?
                                                <div className="flex flex-col h-full justify-center">

                                                    <ActionHandler
                                                        title={appState.t("common.delete")}
                                                        keysTrigger="Delete"
                                                        funq={async () => {
                                                            appState.setModal({
                                                                title: appState.t("common.confirmTitle"),
                                                                body: appState.t("common.confirmIrreversible"),
                                                                action: deleteEntry,
                                                            });
                                                        }}
                                                        disabled={deleting || edit || lock}
                                                    >
                                                        <Remove />
                                                    </ActionHandler>
                                                </div>
                                                :
                                                null
                                        }



                                        {
                                            //  false                       true
                                            updateEntry && !readonly && !(!(edit || forcedEdit) || idParams.singular.get == -1) ?
                                                <div className="flex flex-col h-full justify-center">

                                                    <ActionHandler
                                                        title={appState.t("common.save")}
                                                        keysTrigger="ctrlKey + s"
                                                        funq={async () => {
                                                            if (isRevisionRoot)
                                                                appState.setModal({
                                                                    title: appState.t("common.confirmClearRevisions"),
                                                                    action: () => runInAction(() => {
                                                                        updateEntry();
                                                                    })
                                                                });
                                                            else
                                                                runInAction(() => {
                                                                    updateEntry();
                                                                });
                                                        }}
                                                        disabled={(valid != undefined ? !valid : loading || idParams.singular.get == -1) || !(unsavedChanges || forcedEdit)}
                                                    >
                                                        {isRevisionRoot ? appState.t("common.clearOpenRevisions") : <Save />}
                                                    </ActionHandler>
                                                </div>
                                                :
                                                null

                                        }

                                        {
                                            !chatForm && !readonly && !forcedEdit && !edit && updateEntry && setEdit ?
                                                <div className="flex flex-col h-full justify-center">

                                                    <ActionHandler
                                                        title={appState.t("common.edit")}
                                                        keysTrigger="ctrlKey + e"
                                                        funq={async () => {
                                                            await setEdit(true);
                                                        }}
                                                        disabled={loading || lock}
                                                    >
                                                        <Edit />
                                                    </ActionHandler>
                                                </div>
                                                :
                                                null
                                        }

                                        {
                                            !chatEditorWindowClose && !isModal && !chatForm && !(edit && idParams.singular.get != -1) ?
                                                <div className="flex flex-col h-full justify-center">

                                                    <ActionHandler
                                                        title={appState.t("common.back")}
                                                        keysTrigger="Escape"
                                                        funq={async () => {
                                                            await setEdit(false);

                                                            idParams.singular.set(undefined);
                                                        }}
                                                        disabled={loading}
                                                    >
                                                        <Back />
                                                    </ActionHandler>
                                                </div>
                                                :
                                                null
                                        }

                                        {
                                            createEntry && !readonly && !(idParams.singular.get != -1 || loading) ?
                                                <div className="flex flex-col h-full justify-center">

                                                    <ActionHandler
                                                        title={appState.t("common.save")}
                                                        keysTrigger="ctrlKey + s"
                                                        funq={async () => {
                                                            const x = await createEntry();
                                                            if (x) {
                                                                await setEdit(false);
                                                            }
                                                        }}
                                                        disabled={(valid != undefined ? !valid : loading) || idParams.singular.get != -1}
                                                    >
                                                        <Save />
                                                    </ActionHandler>
                                                </div>
                                                :
                                                null
                                        }



                                        {
                                            edit && !forcedEdit && !chatEditorWindowClose &&
                                            <div className="flex flex-col h-full justify-center">

                                                {/* this discards changes */}
                                                <ActionHandler
                                                    title={appState.t("common.editEditMode")}
                                                    keysTrigger="Escape"
                                                    funq={async () => {
                                                        await setEdit(false);
                                                        setLocalRefreshCount((prev) => prev + 1);
                                                    }}
                                                    disabled={loading || !edit}
                                                    hidden={!edit || idParams.singular.get == -1}
                                                >
                                                    <Close />
                                                </ActionHandler>
                                            </div>
                                        }
                                    </div>
                                )}

                                {/* {chatForm && readonly && (
                                <div className="flex flex-row gap-1">


                                    {
                                        selectFunction ?
                                            <div className="flex flex-col h-full justify-center">

                                                <ActionHandler
                                                    keysTrigger=""
                                                    funq={async (e) => {
                                                        selectFunction(e?.ctrlKey)
                                                    }}
                                                    disabled={false}
                                                >
                                                    <Edit />
                                                </ActionHandler>
                                            </div>
                                            :
                                            null
                                    }



                                </div>
                            )} */}
                                {publishDraft && !readonly && !(idParams.singular.get == -1 || loading) && (
                                    <div className="flex flex-col h-full justify-center">

                                        <ActionHandler
                                            keysTrigger="ctrlKey + s"
                                            funq={async () => {
                                                const x = await publishDraft();
                                                if (x) {
                                                    await setEdit(false);
                                                }
                                            }}
                                            disabled={false}
                                        >
                                            <Publish />
                                        </ActionHandler>
                                    </div>
                                )}

                            </div>



                        )}
                        {/* Multi value selectors */}
                        {/* Single columns with proper type narrowing */}
                        {
                            !loading &&
                            <div className="entry-fields">

                                {stateColumns
                                    ?.filter(x => !x?.hidden && !(x?.readonly && (x.value as Array<any>)?.length === 0)).filter((col): col is StateColumnSingleType => col?.type === "single")
                                    .map((x) => (
                                        <StateColumnSingle key={x.label} {...x} edit={edit} onValidChange={updateValids} />
                                    ))}

                                {/* Multiple columns with proper type narrowing */}
                                {stateColumns
                                    ?.filter(x => !x?.hidden && !(x?.readonly && (x.value as Array<any>)?.length === 0)).filter((col): col is StateColumnSwitchType => col?.type === "switch")
                                    .map((x) => (
                                        <StateColumnSwitch key={x.label} {...x} edit={edit} onValidChange={updateValids} />
                                    ))}
                                {stateColumns
                                    ?.filter(x => !x?.hidden && !(x?.readonly && (x.value as Array<any>)?.length === 0)).filter((col): col is StateColumnMultipleType => col?.type === "multiple")
                                    .map((x) => (
                                        <StateColumnMultiple key={x.label} {...x} edit={edit} onValidChange={updateValids} />
                                    ))}
                                {stateColumns
                                    ?.filter(x => !x?.hidden && !(x?.readonly && (x.value as Array<any>)?.length === 0)).filter((col): col is StateColumnDateTimeRangeType => col?.type === "datetimerange")
                                    .map((x) => (
                                        <StateColumnDateTimeRange key={x.label} {...x} edit={edit} onValidChange={updateValids} />
                                    ))}

                                {/*Buttons */}


                            </div>
                        }

                    </div>
                </div>
            </div>
            {refColumns && (
                <div className={`entry-body ${edit ? "entry-body-editing" : ""}`}>
                    {/* Big fields editor */}
                    {refColumns?.map((x) => {
                        const show = (x.value && x.value.length > 0) || edit;
                        if (!show) return null;

                        return (
                            <div key={x.label} className={`entry-field ${x.type === "richtext" ? "entry-field-rich" : ""}`}>
                                {refColumns.length > 1 && (
                                    <label htmlFor={x.label} className="field-label">
                                        {x.label}
                                    </label>
                                )}
                                {x.type === "richtext" ? (
                                    <RichTextEditor
                                        {...(x as RichTextType)}
                                        edit={edit}
                                        readonly={x.readonly}
                                        onValidChange={updateValids}
                                    />
                                ) : x.type === "textarea" ? (
                                    <EntryTextArea
                                        {...(x as TextAreaType)}
                                        edit={edit}
                                        readonly={x.readonly}
                                        onValidChange={updateValids}
                                    />
                                ) : (
                                    <NameInput
                                        {...(x as TextInputType)}
                                        edit={edit}
                                        readonly={x.readonly}
                                        onValidChange={updateValids}
                                    />
                                )}
                            </div>
                        );
                    })}


                </div>
            )}
        </div>
    );
})
